import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldAlert, LogOut, Loader2, Timer } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

const IDLE_LIMIT_MS = 30 * 60 * 1000;   // 30 Min Inaktivität → Auto-Beenden
const IDLE_WARN_MS = 29 * 60 * 1000;    // 1 Min vorher warnen

const fmtElapsed = (ms) => {
    const s = Math.max(0, Math.floor(ms / 1000));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return h > 0
        ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`
        : `${m}:${String(sec).padStart(2, "0")}`;
};

/**
 * ImpersonationBanner — Jul-2026 (User-Wunsch).
 * Zeigt einen dauerhaften Warnbanner, wenn ein Admin gerade in einem fremden
 * Konto agiert. „Zurück zum Admin" beendet die Impersonation ohne erneuten Login.
 * Der Timer erinnert daran, wie lange man schon im fremden Konto ist.
 * Sicherheit: Nach 30 Min Inaktivität wird der Konto-Wechsel automatisch beendet.
 */
export default function ImpersonationBanner() {
    const { user, stopImpersonate } = useAuth();
    const navigate = useNavigate();
    const [busy, setBusy] = useState(false);
    const [now, setNow] = useState(Date.now());
    const active = !!(user && user.impersonated_by);
    const lastActivityRef = useRef(Date.now());
    const warnedRef = useRef(false);
    const endingRef = useRef(false);

    const stop = useCallback(async (auto = false) => {
        if (endingRef.current) return;
        endingRef.current = true;
        setBusy(true);
        try {
            await stopImpersonate();
            toast.success(auto
                ? "Konto-Wechsel wegen Inaktivität automatisch beendet — zurück im Admin-Konto"
                : "Zurück im Admin-Konto");
            navigate("/admin/dealers");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Wechsel fehlgeschlagen");
            endingRef.current = false;
        } finally {
            setBusy(false);
        }
    }, [stopImpersonate, navigate]);

    // Aktivität erfassen (Maus, Tastatur, Scroll, Touch) → Inaktivitäts-Timer zurücksetzen.
    useEffect(() => {
        if (!active) return undefined;
        const bump = () => { lastActivityRef.current = Date.now(); warnedRef.current = false; };
        const evts = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
        evts.forEach((e) => window.addEventListener(e, bump, { passive: true }));
        return () => evts.forEach((e) => window.removeEventListener(e, bump));
    }, [active]);

    useEffect(() => {
        if (!active) return undefined;
        if (!sessionStorage.getItem("tw_imp_started")) {
            sessionStorage.setItem("tw_imp_started", String(Date.now()));
        }
        lastActivityRef.current = Date.now();
        const t = setInterval(() => {
            setNow(Date.now());
            const idle = Date.now() - lastActivityRef.current;
            if (idle >= IDLE_LIMIT_MS) {
                stop(true);
            } else if (idle >= IDLE_WARN_MS && !warnedRef.current) {
                warnedRef.current = true;
                toast.warning("Konto-Wechsel wird in 1 Minute wegen Inaktivität beendet. Bewege die Maus, um aktiv zu bleiben.");
            }
        }, 1000);
        return () => clearInterval(t);
    }, [active, stop]);

    if (!active) return null;
    const started = Number(sessionStorage.getItem("tw_imp_started")) || now;

    const label = user.company_name || user.name || user.email || "Nutzer";
    return (
        <div
            className="sticky top-0 z-[120] bg-[#B45309] text-white px-4 py-2 flex items-center justify-center gap-3 flex-wrap shadow-md"
            data-testid="impersonation-banner"
        >
            <span className="inline-flex items-center gap-2 text-sm font-bold">
                <ShieldAlert className="w-4 h-4 shrink-0" />
                Admin-Modus — du agierst als <span className="underline underline-offset-2">{label}</span>
                <span className="hidden sm:inline text-white/70 font-normal">({user.role})</span>
            </span>
            <span
                className="inline-flex items-center gap-1 text-xs font-mono-num font-bold bg-black/20 rounded-sm px-2 py-1"
                title="So lange bist du bereits im fremden Konto"
                data-testid="impersonation-timer"
            >
                <Timer className="w-3.5 h-3.5" /> {fmtElapsed(now - started)}
            </span>
            <button
                type="button"
                onClick={() => stop(false)}
                disabled={busy}
                className="inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-wider bg-white text-[#B45309] hover:bg-white/90 rounded-sm px-3 py-1.5 disabled:opacity-60"
                data-testid="impersonation-stop-btn"
            >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogOut className="w-4 h-4" />}
                Zurück zum Admin
            </button>
        </div>
    );
}
